import React, { useState } from 'react';
import {
  ArrowRight, TrendingUp, TrendingDown, Minus,
  Copy, Columns, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { DIMENSION_CONFIG, getScoreGrade, getDimensionScoreColor } from '../../models/analysis';

export function ComparisonPanel({ analysis, originalText }) {
  const [textView, setTextView] = useState('sideBySide');
  const opt = analysis.optimization;
  if (!opt) return null;

  const beforeHQ = opt.beforeScores?.hq_score ?? analysis.hqScore;
  const afterHQ = opt.afterScores?.hq_score ?? beforeHQ;
  const deltaOverall = opt.deltaOverall || 0;
  const deltaDims = opt.deltaDimensions || {};
  const beforeDims = opt.beforeScores?.dimension_scores || {};
  const afterDims = opt.afterScores?.dimension_scores || {};
  const dimKeys = Object.keys(DIMENSION_CONFIG);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-4" data-testid="comparison-panel">
      {/* Score Comparison Header */}
      <div className="msml-panel p-4" data-testid="comparison-score-header">
        <p className="msml-label text-[10px] mb-3">Score Comparison</p>
        <div className="flex items-center justify-center gap-6">
          <ScoreBlock label="Before" score={beforeHQ} />
          <div className="flex flex-col items-center gap-1">
            <ArrowRight className="w-5 h-5 text-[var(--msml-text-muted)]" />
            <DeltaChip delta={deltaOverall} />
          </div>
          <ScoreBlock label="After" score={afterHQ} />
        </div>
      </div>

      {/* Dimension Comparison Grid */}
      <div className="msml-panel p-4" data-testid="comparison-dimensions">
        <p className="msml-label text-[10px] mb-3">Dimension Breakdown</p>
        <div className="space-y-2">
          {dimKeys.map(key => {
            const cfg = DIMENSION_CONFIG[key];
            if (!cfg) return null;
            const dd = deltaDims[key] || {};
            const bScore = dd.before ?? beforeDims[key] ?? 0;
            const aScore = dd.after ?? afterDims[key] ?? bScore;
            const delta = dd.delta ?? (aScore - bScore);
            const maxScore = Math.max(bScore, aScore, 1);
            return (
              <div key={key} className="flex items-center gap-3" data-testid={`comparison-dim-${key}`}>
                <div className="w-24 flex items-center gap-1.5 shrink-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
                  <span className="text-xs text-[var(--msml-text-secondary)] truncate">{cfg.label}</span>
                </div>
                <div className="flex-1 relative h-5">
                  {/* Before bar */}
                  <div
                    className="absolute top-0 left-0 h-2 rounded-full opacity-40"
                    style={{ width: `${(bScore / 100) * 100}%`, backgroundColor: cfg.color }}
                  />
                  {/* After bar */}
                  <div
                    className="absolute bottom-0 left-0 h-2 rounded-full"
                    style={{ width: `${(aScore / 100) * 100}%`, backgroundColor: cfg.color }}
                  />
                </div>
                <div className="w-20 flex items-center gap-1 justify-end shrink-0">
                  <span className="text-[10px] text-[var(--msml-text-muted)]">{Math.round(bScore)}</span>
                  <ArrowRight className="w-2.5 h-2.5 text-[var(--msml-text-muted)]" />
                  <span className="text-[10px] font-semibold" style={{ color: getDimensionScoreColor(aScore) }}>
                    {Math.round(aScore)}
                  </span>
                  {Math.abs(delta) >= 0.5 && (
                    <span className={`text-[9px] font-medium ${delta > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                      {delta > 0 ? '+' : ''}{Math.round(delta)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Text View Toggle */}
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost" size="sm"
          className={`text-xs ${textView === 'sideBySide' ? 'bg-[var(--msml-primary-muted)] text-[var(--msml-primary)]' : 'text-[var(--msml-text-muted)]'}`}
          onClick={() => setTextView('sideBySide')}
          data-testid="compare-side-by-side-btn"
        >
          <Columns className="w-3 h-3 mr-1" /> Side by Side
        </Button>
        <Button
          variant="ghost" size="sm"
          className={`text-xs ${textView === 'stacked' ? 'bg-[var(--msml-primary-muted)] text-[var(--msml-primary)]' : 'text-[var(--msml-text-muted)]'}`}
          onClick={() => setTextView('stacked')}
          data-testid="compare-stacked-btn"
        >
          Stacked
        </Button>
      </div>

      {/* Text Comparison */}
      {textView === 'sideBySide' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="comparison-text-side-by-side">
          <TextBlock
            label="Original"
            text={originalText}
            onCopy={() => handleCopy(originalText, 'Original text')}
            borderColor="var(--msml-text-muted)"
          />
          <TextBlock
            label="Optimized"
            text={opt.text}
            onCopy={() => handleCopy(opt.text, 'Optimized text')}
            borderColor="var(--msml-success)"
          />
        </div>
      ) : (
        <div className="space-y-3" data-testid="comparison-text-stacked">
          <TextBlock
            label="Original"
            text={originalText}
            onCopy={() => handleCopy(originalText, 'Original text')}
            borderColor="var(--msml-text-muted)"
          />
          <TextBlock
            label="Optimized"
            text={opt.text}
            onCopy={() => handleCopy(opt.text, 'Optimized text')}
            borderColor="var(--msml-success)"
          />
        </div>
      )}

      {/* Improvements list */}
      {opt.improvements?.length > 0 && (
        <div className="msml-panel p-4" data-testid="comparison-improvements">
          <p className="msml-label text-[10px] mb-2">Changes Made</p>
          <div className="space-y-1.5">
            {opt.improvements.map((imp, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: imp.color }} />
                <div>
                  <span className="text-[10px] text-[var(--msml-text-muted)] uppercase">{imp.dimensionLabel}</span>
                  <p className="text-xs text-[var(--msml-text-secondary)]">{imp.change}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBlock({ label, score }) {
  const grade = getScoreGrade(score);
  return (
    <div className="text-center" data-testid={`score-block-${label.toLowerCase()}`}>
      <p className="text-3xl font-bold tracking-tight" style={{ color: grade.color }}>
        {Math.round(score)}
      </p>
      <p className="text-[10px] text-[var(--msml-text-muted)] mt-0.5">{label}</p>
      <p className="text-[9px] font-medium mt-0.5" style={{ color: grade.color }}>{grade.label}</p>
    </div>
  );
}

function DeltaChip({ delta }) {
  if (delta == null || Math.abs(delta) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--msml-overlay-white)] text-[var(--msml-text-muted)]">
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const isUp = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
        isUp ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-400'
      }`}
      data-testid="comparison-delta-chip"
    >
      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {isUp ? '+' : ''}{Math.round(delta)}
    </span>
  );
}

function TextBlock({ label, text, onCopy, borderColor }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="msml-panel p-3"
      style={{ borderLeftWidth: '2px', borderLeftColor: borderColor }}
      data-testid={`text-block-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-medium text-[var(--msml-text-muted)] uppercase tracking-wider">{label}</span>
        <div className="flex items-center gap-2">
          <button onClick={onCopy} className="text-[var(--msml-text-muted)] hover:text-[var(--msml-text-primary)] transition-colors" data-testid={`copy-${label.toLowerCase()}-btn`}>
            <Copy className="w-3 h-3" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="text-[var(--msml-text-muted)] hover:text-[var(--msml-text-primary)] transition-colors">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>
      <p className={`text-xs text-[var(--msml-text-secondary)] whitespace-pre-wrap leading-relaxed ${expanded ? '' : 'line-clamp-[8]'}`}>
        {text}
      </p>
    </div>
  );
}
